import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

const NotificationManager = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;

    const isNative = Capacitor.isNativePlatform();
    let pushListeners = [];

    if (isNative) {
      const registerPushNotifications = async () => {
        let permStatus = await PushNotifications.checkPermissions();
        if (permStatus.receive === 'prompt') {
          permStatus = await PushNotifications.requestPermissions();
        }
        if (permStatus.receive !== 'granted') {
          console.warn('User denied push permission');
          return;
        }

        await PushNotifications.register();
      };

      registerPushNotifications();

      const setupListeners = async () => {
        // On success, we should be able to receive notifications
        const registrationListener = await PushNotifications.addListener('registration', async (token) => {
          console.log('Push registration success, token: ' + token.value);
          // Save the token to the database
          await supabase
            .from('device_tokens')
            .upsert({ user_id: user.id, token: token.value }, { onConflict: 'token' });
        });
        pushListeners.push(registrationListener);

        // Some issue with our setup and push will not work
        const registrationErrorListener = await PushNotifications.addListener('registrationError', (error) => {
          console.error('Error on registration: ' + JSON.stringify(error));
        });
        pushListeners.push(registrationErrorListener);

        // Show us the notification payload if the app is open on our device
        const receivedListener = await PushNotifications.addListener('pushNotificationReceived', (notification) => {
          console.log('Push received: ' + JSON.stringify(notification));
          addNotification({
            title: notification.title,
            message: notification.body,
            type: 'info',
            link: notification.data?.url
          });
        });
        pushListeners.push(receivedListener);

        // Method called when tapping on a notification
        const actionPerformedListener = await PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
          console.log('Push action performed: ' + JSON.stringify(notification));
          const url = notification.notification.data?.url;
          if (url) {
            navigate(url);
          }
        });
        pushListeners.push(actionPerformedListener);
      };

      setupListeners();
    } else {
      // Request browser notification permission
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }
    }

    const showBrowserNotification = (title, body, url) => {
      if (!isNative && 'Notification' in window && Notification.permission === 'granted') {
        const notification = new Notification(title, {
          body,
          icon: '/logo.png', // Assuming a logo exists in public
        });
        notification.onclick = () => {
          window.focus();
          if (url) navigate(url);
          notification.close();
        };
      }
    };

    const addNotification = (notif) => {
      const id = Date.now();
      setNotifications(prev => [...prev, { ...notif, id }]);
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== id));
      }, 5000);
    };

    const handleLocalNotification = (e) => {
      const { title, message, type, link } = e.detail;
      addNotification({ title, message, type: type || 'info', link });
    };
    window.addEventListener('app-notification', handleLocalNotification);

    // Listen for new story entries in stories the user is part of
    const entriesChannel = supabase.channel('story-entries-notifications')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'story_entries' }, async (payload) => {
        // Check if user is collaborator or owner
        const { data: story } = await supabase
          .from('stories')
          .select('owner_id')
          .eq('id', payload.new.story_id)
          .single();

        const { data: collab } = await supabase
          .from('collaborators')
          .select('user_id')
          .eq('story_id', payload.new.story_id)
          .eq('user_id', user.id)
          .single();

        if ((story?.owner_id === user.id || collab) && payload.new.role !== 'system') {
            // Don't notify if the user themselves created it (though usually it's tied to their own action, good to filter if possible, but role helps)
            const title = "New Story Entry";
            const body = payload.new.content.substring(0, 50) + '...';
            showBrowserNotification(title, body, `/story`);
            addNotification({ title, message: body, type: 'info', link: '/story' });
        }
      })
      .subscribe();

    // Load missed pokes
    const loadMissedPokes = async () => {
      const { data } = await supabase.from('pokes').select('*').eq('receiver_id', user.id);
      if (data && data.length > 0) {
        data.forEach(poke => {
          const title = "You were poked!";
          const body = `${poke.sender_name} poked you in "${poke.story_title}"`;
          const link = poke.story_id ? `/story?id=${poke.story_id}` : '/story';
          showBrowserNotification(title, body, link);
          addNotification({ title, message: body, type: 'info', link });
          supabase.from('pokes').delete().eq('id', poke.id).then();
        });
      }
    };
    loadMissedPokes();

    // Listen for pokes
    const pokesChannel = supabase.channel(`pokes-notifications-${user.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'pokes' }, (payload) => {
        if (payload.new.receiver_id === user.id) {
          const title = "You were poked!";
          const body = `${payload.new.sender_name} poked you in "${payload.new.story_title}"`;
          const link = payload.new.story_id ? `/story?id=${payload.new.story_id}` : '/story';
          showBrowserNotification(title, body, link);
          addNotification({ title, message: body, type: 'info', link });
          supabase.from('pokes').delete().eq('id', payload.new.id).then();
        }
      })
      .subscribe();

    // Listen for collaboration invites
    const collabsChannel = supabase.channel('collabs-notifications')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'collaborators' }, async (payload) => {
        if (payload.new.user_id === user.id && payload.new.status === 'Invited') {
          const title = "Collaboration Invite";
          const body = "You have been invited to collaborate on a story.";
          showBrowserNotification(title, body, `/collab/${payload.new.story_id}`);
          addNotification({ title, message: body, type: 'success', link: `/collab/${payload.new.story_id}` });
        } else if (payload.new.status === 'Pending') {
           // Notify the owner of the story about a request to join
           const { data: story } = await supabase
            .from('stories')
            .select('owner_id, title')
            .eq('id', payload.new.story_id)
            .single();

           if (story?.owner_id === user.id) {
               let charName = "Someone";
               let username = "Unknown";

               if (payload.new.character_id) {
                 const { data: char } = await supabase.from('characters').select('name').eq('id', payload.new.character_id).single();
                 if (char) charName = char.name;
               }

               if (payload.new.user_id) {
                 const { data: profile } = await supabase.from('profiles').select('username').eq('id', payload.new.user_id).single();
                 if (profile) username = profile.username;
               }

               const title = "Request to Join";
               const body = `${charName} (@${username}) has requested to join your story "${story.title || 'Untitled'}".`;
               showBrowserNotification(title, body, `/dashboard?tab=characters`);
               addNotification({ title, message: body, type: 'info', link: `/dashboard?tab=characters` });
           }
        }
      })
      .subscribe();

    return () => {
      window.removeEventListener('app-notification', handleLocalNotification);
      supabase.removeChannel(entriesChannel);
      supabase.removeChannel(collabsChannel);
      supabase.removeChannel(pokesChannel);
      if (isNative) {
        pushListeners.forEach(listener => listener.remove());
      }
    };
  }, [user, navigate]);

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col space-y-2 pointer-events-none">
      <AnimatePresence>
        {notifications.map((notif) => (
          <motion.div
            key={notif.id}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className={`pointer-events-auto p-4 rounded-xl shadow-lg flex items-start space-x-3 max-w-sm cursor-pointer ${
              notif.type === 'success'
                ? 'bg-green-100 dark:bg-green-900 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200'
                : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200'
            }`}
            onClick={() => {
              if (notif.link) navigate(notif.link);
            }}
          >
            <div className="mt-1">
              {notif.type === 'success' ? (
                <SafeIcon icon={FiIcons.FiCheckCircle} className="text-xl" />
              ) : (
                <SafeIcon icon={FiIcons.FiBell} className="text-xl text-gray-700" />
              )}
            </div>
            <div>
              <h4 className="font-bold text-sm">{notif.title}</h4>
              <p className="text-xs opacity-90">{notif.message}</p>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default NotificationManager;
